<template>
  <div>
    <div class="table-responsive">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>{{$t('title')}}</th>
            <th>{{$t('status')}}</th>
            <th>{{$t('priority')}}</th>
            <th>{{$t('progress')}}</th>
            <th>{{$t('due_date')}}</th>
            <th>{{$t('assigned_to')}}</th>
            <th style="width: 250px;">{{$t('action')}}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="task in tasks" :key="task.id">
            <td>{{ task.title }}</td>
            <td>
              <span class="badge" :style="{ backgroundColor: getStatusColor(task.status), color: '#fff' }">
                {{ getStatusLabel(task.status) }}
              </span>
            </td>
            <td>
              <span :class="'badge bg-' + getPriorityColor(task.priority)">
                {{ $t(task.priority) }}
              </span>
            </td>
            <td>
              <div class="progress" style="height: 20px; width: 80px;">
                <div class="progress-bar" :style="{width: task.progress + '%'}">
                  {{ task.progress }}%
                </div>
              </div>
            </td>
            <td>{{ formatDate(task.due_date) }}</td>
            <td>
              <span v-if="task.assignments && task.assignments.length">
                {{ task.assignments.map(a => a?.user?.name).join(', ') }}
              </span>
              <span v-else class="text-muted">{{$t('unassigned')}}</span>
            </td>
            <td> 
              <div class="d-flex gap-1 flex-wrap">
                <button v-if="helper.checkAccess('view_tasks') > 0" @click="viewTask(task)" style="padding: 5px;font-size: 12px !important;" class="btn btn-primary btn-xs" title="View Details">
                  <AppIcon name="bi-eye" class="bi bi-eye" /> {{$t('details')}}
                </button>
                <button v-if="helper.checkAccess('edit_tasks') > 0" @click="editTask(task)" style="padding: 5px;font-size: 12px !important;" class="btn btn-primary btn-xs" title="Edit Task">
                  <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" /> {{$t('edit')}}
                </button>
                <button v-if="helper.checkAccess('delete_tasks') > 0" @click="deleteTask(task)" style="padding: 5px;font-size: 12px !important;" class="btn btn-primary btn-xs" title="Delete Task">
                  <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" /> {{$t('delete')}}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <table-loading-bar v-if="loading" class="text-center py-3" />
      <div v-if="!loading && !tasks.length" class="text-center py-3">{{$t('no_tasks_found')}}</div>
    </div>

    <!-- Task Detail Modal -->
    <task-detail-modal v-if="selectedTask" :task="selectedTask" @close="selectedTask = null" @refresh="loadTasks"></task-detail-modal>
    
    <!-- Add/Edit Task Modal -->
    <add-task-modal v-if="editingTask" :projectId="projectId" :task="editingTask" @close="closeTaskModal" @refresh="loadTasks"></add-task-modal>
  </div>
</template>

<script>
import helper from '../../helper'
import TaskDetailModal from './TaskDetailModal.vue'
import AddTaskModal from './AddTaskModal.vue'
import { getTaskStatusColor, getTaskStatusLabel } from '../../helpers/taskStatusHelper'

export default {
  name: 'TaskList',
  components: {
    TaskDetailModal,
    AddTaskModal
  },
  props: {
    projectId: {
      type: String,
      required: true
    },

    tasks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      helper,
      loading: false,
      selectedTask: null,
      editingTask: null,
      quickLogTask: null
    }
  },

  mounted() {
    // Tasks are loaded via prop
  },
  methods: {
    async loadTasks() {
      this.$emit('refresh')
    },
    async viewTask(task) {
      if (helper.checkAccess('view_tasks') <= 0) return
      // Fetch full task details including activities and attachments
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/tasks/${task.id}`)
        this.selectedTask = response.data
      } catch (error) {
        alert('Error loading task details:', error)
      }
    },
    async quickLogTime(task) {
      const hours = prompt(this.$t('enter_hours_worked'), '1')
      if (hours && parseFloat(hours) > 0) {
        const description = prompt(this.$t('enter_description_optional'), '')
        try {
          await helper.customPost(`${process.env.VUE_APP_API}/tasks/${task.id}/log-time`, {
            hours: parseFloat(hours),
            description: description || ''
          }, { showToast: true })
          this.loadTasks()
        } catch (error) {
          alert('Error logging time:', error)
        }
      }
    },
    async editTask(task) {
      if (helper.checkAccess('edit_tasks') <= 0) return
      // Fetch full task details including assignments and tags
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/tasks/${task.id}`)
        this.editingTask = response.data
      } catch (error) {
        alert('Error loading task details:', error)
      }
    },
    async deleteTask(task) {
      if (helper.checkAccess('delete_tasks') <= 0) return
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      })
      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/tasks/${task.id}`, { showToast: true })
          this.$emit('deleted', task.id)
        } catch (error) {
          alert('Error deleting task:', error)
        }
      }
    },
    closeTaskModal() {
      this.editingTask = null
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString()
    },
    getStatusColor(status) {
      return getTaskStatusColor(this.$store, status);
    },
    getStatusLabel(status) {
      return getTaskStatusLabel(this.$store, status);
    },
    getPriorityColor(priority) {
      const colors = {
        low: 'info',
        medium: 'primary',
        high: 'warning',
        urgent: 'danger'
      }
      return colors[priority] || 'primary'
    }
  }
}
</script>
